import { Box, Container, useMediaQuery } from '@material-ui/core'
import Grid from '@material-ui/core/Grid'
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles'
import Breadcrumb from '@src/components/Breadcrumb'
import { Chat, ChatChannel } from '@src/components/Chat'
import DefaultLayout, {
  LayoutSpacingOptions,
} from '@src/components/DefaultLayout'
import GlobalAlert from '@src/components/GlobalAlert'
import { useRouter } from '@src/routes'
import vrStyles from '@src/styles/utils/vrStyles'
import { useOrderCenterBreadCrumbItems } from '@src/utils/orderCenterUtils'
import { useCurrentUser } from '@src/utils/useCurrentUser'
import { Breakpoints } from '@src/utils/useResponsiveSizes'
import clsx from 'clsx'
import dynamic from 'next/dynamic'
import { ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import CustomerLogo from './CustomerLogo'
import messages from './messages'

const { vr2 } = vrStyles

const useStyles = makeStyles((theme: Theme) => {
  return {
    vr2,
    root: {
      padding: 0,
      [theme.breakpoints.up('md')]: {
        padding: theme.spacing(8, 0),
      },
    },
    nav: {
      // necessary to override mui grid item padding for proper alignment
      paddingTop: '0 !important',
    },
    content: {
      padding: 0,
      [theme.breakpoints.up('md')]: {
        padding: theme.spacing(0, 4),
      },
    },
    layoutContainer: {
      padding: 0,
      [theme.breakpoints.up('md')]: {
        padding: theme.spacing(0, 4),
      },
    },
    whiteBg: {
      backgroundColor: theme.palette.common.white,
    },
    breadcrumbContainer: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
      margin: theme.spacing(6, 4, 2, 4),
      [theme.breakpoints.up('md')]: {
        margin: theme.spacing(0, 0, 10, 0),
      },
    },
    returnSubmitted: {
      marginBottom: theme.spacing(8),
    },
    logo: {
      padding: theme.spacing(5),
      marginBottom: theme.spacing(10),
    },
  }
})

const OrderCenterNav = dynamic(
  () => import('@src/routes/OrderCenter/Nav/OrderCenterNav'),
  { ssr: false }
)

interface OrderCenterLayoutProps {
  isDetail?: boolean
  isLogoEnabled?: boolean
  isNavEnabled?: boolean
  isQuickOrderLayout?: boolean
  showRecentlyViewedProducts?: boolean
  children: ReactNode
}

const OrderCenterLayout = ({
  children,
  isDetail = false,
  isLogoEnabled = true,
  isNavEnabled = true,
}: OrderCenterLayoutProps) => {
  const classes = useStyles()
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up(Breakpoints.MD), {
    defaultMatches: true,
  })
  const router = useRouter()
  const { currentUser } = useCurrentUser()

  const breadCrumbItems = useOrderCenterBreadCrumbItems(formatMessage)

  const layoutSpacing =
    isDetail || !isDesktop ? LayoutSpacingOptions.NONE : LayoutSpacingOptions.MD

  const logoDirectory =
    currentUser?.__typename === 'LoggedInUser' &&
    currentUser.b2bChannelInformation &&
    currentUser.b2bChannelInformation.logoDirectory

  return (
    <Box className={clsx(classes.root, classes.whiteBg)}>
      <DefaultLayout
        spacing={layoutSpacing}
        className={clsx(classes.root, classes.whiteBg)}
      >
        <Container maxWidth="lg" className={classes.layoutContainer}>
          {!isDesktop && <OrderCenterNav />}

          <div className={classes.breadcrumbContainer}>
            {breadCrumbItems.length > 0 && (
              <Breadcrumb items={breadCrumbItems} />
            )}
          </div>

          {!!router?.query?.returnSubmitted && (
            <GlobalAlert severity="info" className={classes.returnSubmitted}>
              <FormattedMessage {...messages.ORDER_RETURN_SUBMITTED} />
            </GlobalAlert>
          )}
          {!!router?.query?.cancelOrderSubmitted && (
            <GlobalAlert severity="info" className={classes.returnSubmitted}>
              <FormattedMessage {...messages.ORDER_CANCEL_SUBMITTED} />
            </GlobalAlert>
          )}
          <Grid container spacing={isDesktop ? 8 : 0}>
            {!isDetail && isNavEnabled && (
              <Grid item xs={12} md={3} className={classes.nav}>
                {isDesktop && <OrderCenterNav />}
              </Grid>
            )}
            <Grid
              item
              xs={12}
              md={!isDetail && isNavEnabled ? 9 : 12}
              className={classes.content}
            >
              {logoDirectory && !isDetail && isLogoEnabled && (
                <div className={classes.logo}>
                  <CustomerLogo />
                </div>
              )}
              {children}
            </Grid>
          </Grid>
          <Chat
            text={formatMessage({
              id: 'COMMERCE_CHAT_CUSTOMER_CARE',
              defaultMessage: 'Chat with Customer Care',
            })}
            channel={ChatChannel.CustomerService}
          />
        </Container>
      </DefaultLayout>
    </Box>
  )
}

export default OrderCenterLayout
